import { useState, useEffect, useCallback, useRef } from 'react';
import { currentConfig } from '../config/environment';
import { getEffectiveUserId } from '../services/accountScopedDataService';
import { getCurrentUser } from '../services/authorizationService';

export const useGoogleOAuthStatus = () => {
  const [isConnected, setIsConnected] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastChecked, setLastChecked] = useState(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const retryCountRef = useRef(0);
  const effectiveUserId = getEffectiveUserId(); // Use account-scoped user ID for data operations
  const currentUser = getCurrentUser(); // Get admin's actual UID for OAuth operations
  const adminUserId = currentUser?.uid; // Admin's actual UID for OAuth

  const MAX_RETRIES = 3;
  const RETRY_DELAY = 2000; // 2 seconds

  const checkOAuthStatus = useCallback(async (isRetry = false) => {
    const checkId = Math.random().toString(36).substr(2, 9);
    console.log(`[${checkId}] Starting OAuth status check, isRetry: ${isRetry}`);
    
    if (!adminUserId) {
      console.log(`[${checkId}] No adminUserId, setting disconnected`);
      setIsConnected(false);
      setIsLoading(false);
      return;
    }

    try {
      if (!isRetry) {
        setIsLoading(true);
      }
      setError(null);
      
      // Use admin's UID for OAuth status check, but include account scope
      const params = new URLSearchParams({
        userId: adminUserId
      });
      
      // If we're scoped to a client account, include that in the request
      if (effectiveUserId && effectiveUserId !== adminUserId) {
        params.append('accountId', effectiveUserId);
      }
      
      console.log(`[${checkId}] Checking OAuth status with params:`, params.toString());
      
      // Add cache-busting parameter to force fresh request
      params.append('_t', Date.now().toString());
      
      const response = await fetch(
        `${currentConfig.apiBaseUrl}/api/oauth/status?${params.toString()}`,
        {
          method: 'GET',
          cache: 'no-cache',
          headers: {
            'Cache-Control': 'no-cache'
          }
        }
      );
      
      console.log(`[${checkId}] OAuth status response status:`, response.status);
      
      if (!response.ok) {
        if (response.status === 401) {
          // Token expired or invalid
          setIsConnected(false);
          setError('Authentication expired. Please reconnect your Google account.');
        } else if (response.status === 429) {
          // Rate limited
          throw new Error('Rate limited. Please try again later.');
        } else {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        return;
      }
      
      const data = await response.json();
      
      console.log(`[${checkId}] OAuth status response:`, data);
      
      // Validate response data
      if (data && typeof data.connected === 'boolean') {
        console.log(`[${checkId}] Setting OAuth status to:`, data.connected);
        setIsConnected(data.connected);
        setLastChecked(new Date());
        retryCountRef.current = 0; // Reset retry count on success
        
        if (data.connected && data.message) {
          console.log(`[${checkId}] OAuth status:`, data.message);
        }
      } else {
        throw new Error('Invalid response format from OAuth status endpoint');
      }
      
    } catch (err) {
      console.error('Failed to check OAuth status:', err);
      setError(err.message);
      
      // Implement retry logic for network errors
      if (!isRetry && retryCountRef.current < MAX_RETRIES && 
          (err.message.includes('Failed to fetch') || err.message.includes('Network'))) {
        retryCountRef.current += 1;
        setIsRetrying(true);
        
        setTimeout(() => {
          setIsRetrying(false);
          checkOAuthStatus(true);
        }, RETRY_DELAY * retryCountRef.current);
        
        return;
      }
      
      // If all retries failed or it's not a network error, set as disconnected
      if (retryCountRef.current >= MAX_RETRIES || !err.message.includes('Failed to fetch')) {
        setIsConnected(false);
      }
    } finally {
      if (!isRetry) {
        setIsLoading(false);
      }
    }
  }, [adminUserId, effectiveUserId]);

  const startOAuth = useCallback(() => {
    if (!adminUserId) {
      setError('User not authenticated');
      return;
    }
    
    try {
      // Clear any existing errors
      setError(null);
      
      // Use admin's UID for OAuth start, but include account scope
      const params = new URLSearchParams({
        userId: adminUserId
      });
      
      // If we're scoped to a client account, include that in the request
      if (effectiveUserId && effectiveUserId !== adminUserId) {
        params.append('accountId', effectiveUserId);
      }
      
      // Redirect to Worker's OAuth start endpoint
      const oauthUrl = `${currentConfig.apiBaseUrl}/api/oauth/google/start?${params.toString()}`;
      window.location.href = oauthUrl;
    } catch (err) {
      console.error('Failed to start OAuth:', err);
      setError('Failed to start OAuth process. Please try again.');
    }
  }, [adminUserId, effectiveUserId]);

  const disconnectOAuth = useCallback(async () => {
    if (!adminUserId) return;
    
    try {
      setError(null);
      
      // Use admin's UID for OAuth disconnect, but include account scope
      const params = new URLSearchParams({
        userId: adminUserId
      });
      
      // If we're scoped to a client account, include that in the request
      if (effectiveUserId && effectiveUserId !== adminUserId) {
        params.append('accountId', effectiveUserId);
      }
      
      // Clear tokens from Worker KV
      console.log('Disconnecting OAuth with params:', params.toString());
      const response = await fetch(
        `${currentConfig.apiBaseUrl}/api/oauth/disconnect?${params.toString()}`,
        { 
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Disconnect response status:', response.status);
      
      if (response.ok) {
        setIsConnected(false);
        setLastChecked(new Date());
        retryCountRef.current = 0;
        console.log('OAuth disconnected successfully');
        
        // Clear any stored Google tokens from localStorage
        localStorage.removeItem('google_token');
        localStorage.removeItem('google_user');
        console.log('Cleared Google tokens from localStorage');
        
        // Force a fresh status check to confirm disconnection
        setTimeout(() => {
          console.log('Forcing OAuth status check after disconnect...');
          checkOAuthStatus();
        }, 1000);
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }
    } catch (err) {
      console.error('Failed to disconnect OAuth:', err);
      setError(err.message);
    }
  }, [adminUserId, effectiveUserId, checkOAuthStatus]);

  const forceRefresh = useCallback(async () => {
    retryCountRef.current = 0;
    setError(null);
    console.log('🔄 Force refreshing OAuth status...');
    await checkOAuthStatus(true); // Pass true to force refresh
  }, [checkOAuthStatus]);

  // Check status on mount and when userId changes
  useEffect(() => {
    console.log('🔄 OAuth status hook mounted, checking status...');
    checkOAuthStatus();
  }, [checkOAuthStatus]);

  // Auto-refresh status every 5 minutes if connected
  useEffect(() => {
    if (!isConnected) return;
    
    const interval = setInterval(() => {
      checkOAuthStatus();
    }, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [isConnected, checkOAuthStatus]);

  // Disabled auto-refresh for disconnected state to prevent automatic reconnection
  // useEffect(() => {
  //   if (isConnected !== false) return;
  //   
  //   const interval = setInterval(() => {
  //     checkOAuthStatus();
  //   }, 30 * 1000);
  //   
  //   return () => clearInterval(interval);
  // }, [isConnected, checkOAuthStatus]);

  return {
    isConnected,
    isLoading: isLoading || isRetrying,
    error,
    lastChecked,
    isRetrying,
    retryCount: retryCountRef.current,
    checkOAuthStatus,
    startOAuth,
    disconnectOAuth,
    refresh: checkOAuthStatus,
    forceRefresh
  };
};
